import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Pagination,
  Tooltip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';

/**
 * Return Entry List component
 * 
 * @param {Object} props - Component props
 * @param {Array} props.returnLogs - Array of return log entries
 * @param {boolean} props.loading - Whether data is loading
 * @param {string} props.error - Error message if any
 * @param {function} props.onRefresh - Function to call to refresh data
 * @param {function} props.onDelete - Function to call when delete is clicked
 * @param {number} props.page - Current page number
 * @param {function} props.onPageChange - Function to call when page changes
 * @param {number} props.totalCount - Total number of records
 * @param {number} props.rowsPerPage - Number of rows per page
 * @returns {JSX.Element} - The return entry list/table
 */
const ReturnEntryList = ({
  returnLogs = [],
  loading = false,
  error = null,
  onRefresh,
  onDelete,
  page = 1,
  onPageChange,
  totalCount = 0,   
  rowsPerPage = 20
}) => {
  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <Paper elevation={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
        <Typography variant="h6" component="h2">
          Return Entries
        </Typography>
        
        <Button
          startIcon={<RefreshIcon />}
          onClick={onRefresh}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Marketplace</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>AWB</TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Created At</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && !returnLogs.length ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: 'error.main', py: 3 }}>
                  {error}
                </TableCell>
              </TableRow>
            ) : returnLogs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  No return entries found
                </TableCell>
              </TableRow>
            ) : (
              returnLogs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell>{log.marketplace}</TableCell>
                  <TableCell>{log.company}</TableCell>
                  <TableCell>{log.AWB}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }} >{formatDate(log.created_at)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Delete entry">
                      <IconButton 
                        color="error"
                        onClick={() => onDelete(log.id)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {totalCount > rowsPerPage && (
        <Box display="flex" justifyContent="center" p={2}>
          <Pagination
            count={Math.ceil(totalCount / rowsPerPage)}
            page={page}
            onChange={onPageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Paper>
  );
};

export default ReturnEntryList;
